import React, { FC, memo, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Form } from '@components/atoms/Form';
import { Input, InputTypes, InputVariants } from '@components/atoms/Input';
import { CheckboxFilter } from '../CheckboxFilter';
import { RangeFilter } from '../RangeFilter';
import { useData } from '@hooks/useData';
import { useFilterBuilder } from '@hooks/useFilterBuilder';
import { useFilters } from '@hooks/useFilters';
import { flattenCategories } from '@utils/helper';
import { useFilterBuilderText } from '@hooks/useFilterBuilderText';
import { useFilterBuilderPrice } from '@hooks/useFilterBuilderPrice';
import { ProductCardProps } from '@components/molecules/ProductCard';
import { useGetPointsServices } from '@services/useGetPointsServices';
import { useGetAllCategoriesWithPointsServices } from '@services/useGetAllCategoriesWithPointsServices';
import { useGetPointsServicesMaxPoints } from '@services/useGetPointsServicesMaxPoints';

export interface FilterSidebarPointsServicesProps {
    pageToRequest: number;
    checkPageToRequest: () => void;
}

export const FilterSidebarPointsServices: FC<FilterSidebarPointsServicesProps> = ({ pageToRequest, checkPageToRequest }) => {
    const location = useLocation();
    const { searchQuery } = useParams();
    const {
        allCategoriesWithPointsServices,
        isLoadingAllCategoriesWithPointsServices,
        pointsServices,
        isLoadingPointsServices,
        setPointsServices,
    } = useData();
    const { request: requestAllCategoriesWithPointsServices } = useGetAllCategoriesWithPointsServices();
    const { request: requestPointsServices } = useGetPointsServices();
    const { appliedFilters, results, setResults, currentPage, setCurrentPage } = useFilters();
    const { i18n } = useTranslation();

    useEffect(() => {
        // when unmounting page, reset results
        // to prevent showing old results when returning to this page
        return () => {
            setResults([]);
            setPointsServices(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // call services, if we don't have the information yet
    useEffect(() => {
        !allCategoriesWithPointsServices && !isLoadingAllCategoriesWithPointsServices && requestAllCategoriesWithPointsServices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allCategoriesWithPointsServices]);

    const formRef = useRef<HTMLFormElement>(null);

    const queryParams = new URLSearchParams(location.search);
    const getTextParam = queryParams.get('text') || '';
    const getCategoriesParam = queryParams.get('categories') || '';
    const getMinParam = queryParams.get('min') || '';
    const getMaxParam = queryParams.get('max') || '';

    const fetchPointsServices = (page: number) => {
        setCurrentPage(page);

        // prevent calls for pages that don't exist
        if (pointsServices?.pagination?.total_pages && page > pointsServices?.pagination?.total_pages) {
            return;
        }

        const textSearch = searchQuery || getTextParam;
        const categoriesUuids = getCategoriesParam ? getCategoriesParam.split(',') : [];

        requestPointsServices({
            textSearch: textSearch,
            categories_uuid: categoriesUuids,
            min_points: Number(getMinParam) || undefined,
            max_points: Number(getMaxParam) || undefined,
            page: page,
        });
    };

    // request more services from next page
    useEffect(() => {
        pageToRequest > currentPage && !isLoadingPointsServices && fetchPointsServices(currentPage + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageToRequest]);

    // request first page whenever a filter changes
    const submitForm = () => fetchPointsServices(1);

    // if selected language changes, starr from page 1
    useEffect(() => {
        setResults([]);
        submitForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    useEffect(() => {
        !isLoadingPointsServices && formRef?.current?.requestSubmit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedFilters, searchQuery]);

    // every time the endpoint is called, append the data to results constant
    // if the page being called is the first page, reset results
    useEffect(() => {
        setResults((previous: ProductCardProps[]) => {
            const currentPageData = pointsServices?.data || [];

            return pointsServices?.pagination?.current_page === 1 ? currentPageData : [...previous, ...currentPageData];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pointsServices, i18n.language]);

    // manually check if next page should be requested
    // in case user's screen is big and isn't possible to scroll to trigger next page request
    useEffect(() => {
        checkPageToRequest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results.length]);

    return (
        <>
            <Form formRef={formRef} classes="d-flex flex-column gap-4" onSubmit={submitForm}>
                <TextFilter />
                <CategoriesFilters />
                <AmountFilter />
            </Form>
        </>
    );
};

const TextFilter: FC = () => {
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const translations = {
        title: t('filter-sidebar-points-services-components.filter-text-search.title'),
        placeholder: t('filter-sidebar-points-services-components.text-search.placeholder'),
    };

    const paramName = 'text';
    const title = translations.title;

    const { search, setSearch } = useFilterBuilderText({
        paramName,
        appliedValue: appliedFilters.find((item) => item.label === title)?.values[0].name || '',
        setFilterValues: (data) => setFilterValues(title, data),
    });

    return (
        <Input
            type={InputTypes.text}
            variant={InputVariants.searchInput}
            placeholder={translations.placeholder}
            value={search || ''}
            onChange={(value) => setSearch(value)}
        />
    );
};

const AmountFilter: FC = memo(() => {
    const { request: requestMaxPoints, loading: loadingMaxPoints } = useGetPointsServicesMaxPoints();
    const { pointsServicesMaxPoints } = useData();
    const { appliedFilters, setFilterValues } = useFilters();

    const { t } = useTranslation();

    const translations = {
        title: t('filter-sidebar-points-services-components.filter-range.title'),
    };

    useEffect(() => {
        requestMaxPoints();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const title = translations.title;

    const appliedValue = appliedFilters.find((item) => item.label === title)?.values[0].name || '';
    const splitAppliedValues = appliedValue.split(' - ');
    const appliedMinValue = appliedValue ? splitAppliedValues[0]?.trim() || '' : '';
    const appliedMaxValue = appliedValue ? splitAppliedValues[1]?.trim() || '' : '';

    const maxPoints = pointsServicesMaxPoints?.data?.max ? Number(pointsServicesMaxPoints?.data?.max) : null;

    const { min, max, setMin, setMax } = useFilterBuilderPrice({
        appliedValues: { min: appliedValue ? Number(appliedMinValue) : null, max: appliedValue ? Number(appliedMaxValue) : null },
        setFilterValues: (min, max) =>
            setFilterValues(
                title,
                min === null && max === null ? [] : [{ uuid: null, name: `${min || 0} - ${max || maxPoints}` }]
            ),
    });

    return (
        <>
            {maxPoints && !loadingMaxPoints && (
                <RangeFilter
                    maxInput={maxPoints}
                    minValue={Number(min) || 0}
                    maxValue={Number(max) || maxPoints}
                    step={5}
                    setMinValue={setMin}
                    setMaxValue={setMax}
                />
            )}
        </>
    );
});

const CategoriesFilters: FC = () => {
    const { allCategoriesWithPointsServices } = useData();
    const { request: requestAllCategoriesWithPointsServices } = useGetAllCategoriesWithPointsServices();
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const translations = {
        title: t('filter-sidebar-points-services-components.checkbox-categories.title'),
    };

    const paramName = 'categories';
    const title = translations.title;

    const { getOptions, toggleValue } = useFilterBuilder([
        {
            paramName,
            labelField: 'name',
            data: { data: flattenCategories(allCategoriesWithPointsServices?.data || []) },
            requestData: requestAllCategoriesWithPointsServices,
            appliedValues: appliedFilters.find((item) => item.label === title)?.values.map((value) => value.name) || [],
            setFilterValues: (data) => setFilterValues(title, data),
        },
    ]);

    const options = getOptions(paramName);

    return (
        <>{options && <CheckboxFilter title={title} options={options} onChange={(value) => toggleValue(paramName, value)} />}</>
    );
};
